import { useId } from 'react';
import { cx } from './cx.js';
import { FieldLabel } from './FieldLabel.js';
import { TextInput, type TextInputProps } from './TextInput.js';

/**
 * A text field with its line underneath: a hint, or the reason it was refused.
 *
 * `TextInput` draws the field and cannot wire what sits under it, because it
 * does not know what the line says. Seven copies did that wiring by hand, and
 * they had drifted three ways: the hint was `text-muted`, `text-sm text-muted`
 * or `text-sm text-gray-700`; a refusal was a `Banner`, a red line or a muted
 * one; and only some pointed `aria-describedby` at the line at all, so a
 * screen reader heard the field without the sentence drawn under it.
 *
 * This owns the three things a copy forgets: the id, `aria-describedby`, and
 * `aria-invalid`. An `error` takes the hint's place, in red, with the field's
 * stroke (Mitch, 2026-09-21). One line, never two: a refused field says why it
 * was refused, and the hint is back when the error clears.
 *
 * `label` draws a visible `FieldLabel` above the field and names it with a
 * `<label htmlFor>`. It used to wrap the field, which was fine until the
 * search register put a button inside: a wrapping label's name is all of its
 * text, so a searchbox labeled "Rules" read as "Rules Clear search" whenever it
 * held text. Omit `label` for a field named some other way, and pass
 * `aria-label` or `aria-labelledby` instead.
 */
interface FieldLine {
  /** The visible name above the field. Already translated. */
  readonly label?: string | undefined;
  /** One line under the field. Already translated. */
  readonly hint?: string | undefined;
  /** Why the field was refused. Replaces the hint and turns the field red. */
  readonly error?: string | undefined;
  /** Layout only: where the field sits and how wide it is. */
  readonly className?: string | undefined;
}

/**
 * Omitted from each of `TextInput`'s registers on its own. A plain `Omit`
 * over the union would keep only the keys the registers share, and a search
 * field would lose the `value` and `onClear` it cannot be drawn without.
 */
type OwnedByTextField<P> = P extends unknown
  ? Omit<P, 'aria-describedby' | 'aria-invalid' | 'className'>
  : never;

export type TextFieldProps = OwnedByTextField<TextInputProps> & FieldLine;

export function TextField({
  label,
  hint,
  error,
  className,
  ...rest
}: TextFieldProps): React.JSX.Element {
  const lineId = useId();
  const generatedId = useId();
  const fieldId = rest.id ?? generatedId;
  const line = error ?? hint;
  const input = (
    <TextInput
      {...rest}
      id={fieldId}
      aria-invalid={error !== undefined}
      {...(line === undefined ? {} : { 'aria-describedby': lineId })}
    />
  );
  return (
    <div className={cx('flex flex-col gap-2', className)}>
      {label !== undefined && (
        <label htmlFor={fieldId}>
          <FieldLabel className="w-full">{label}</FieldLabel>
        </label>
      )}
      {input}
      {line !== undefined && (
        <p className={error === undefined ? 'text-muted' : 'text-red'} id={lineId}>
          {line}
        </p>
      )}
    </div>
  );
}
